import React, {useMemo} from 'react';
import {List, Map} from 'immutable';
import {Color, PctDouble, Seconds} from '@store/@types/util';
import {ExpandToggle} from '@components/util/ExpandToggle';
import {CallSpeakerFull} from '@components/call-card/CallSpeakerFull';
import {CallSpeakerMini} from '@components/call-card/CallSpeakerMini';
import {
  DragStatus,
  Segment,
  Speaker,
  SpeakerNumber,
  TopicMatch,
} from '@store/call-card/CallCard.types';
import IntervalTree from 'node-interval-tree';
import {PlayerProps} from '@components/call-card/Player.utils';
import {TopicId} from '@store/topic-keyword/TopicKeyword.types';
import {combineMatchesBySpeaker} from '@components/call-card/TranscriptMatch.utils';
import {MiniSpeakerNumber} from './CallSpeaker.utils';
import User, {UserId} from 'records/User';
import {SetState} from '@store/@types/util/React';

interface CallSpeakerSectionProps extends PlayerProps {
  speakers: List<Speaker>;
  talkTime: Map<SpeakerNumber, Seconds>;
  segments: List<Segment>;
  setTimePct: (pct: PctDouble) => void;
  matches: Map<TopicId, List<TopicMatch>>;
  activeTopics: Map<TopicId, Color>;
  callerId: UserId;
  dragStatus: DragStatus;
  setDragStatus: SetState<DragStatus>;
  sessionUser?: User;
}

export const CallSpeakerSection: React.FC<CallSpeakerSectionProps> = ({
  segments,
  talkTime,
  speakers,
  setTimePct,
  matches,
  activeTopics,
  sessionUser,
  callerId,
  dragStatus,
  setDragStatus,
  ...playerProps
}) => {
  const speakerTracks = useMemo(
    () => buildSpeakerTracks(segments, playerProps.duration),
    [segments, playerProps.duration]
  );

  const segmentTree = useMemo(() => {
    const tree = new IntervalTree<SpeakerNumber, Seconds>();
    for (const segment of segments) {
      tree.insert(segment.startTime, segment.endTime, segment.speakerNumber);
      tree.insert(segment.startTime, segment.endTime, MiniSpeakerNumber);
    }
    return tree;
  }, [segments]);

  const speakerMatches = useMemo(
    () => combineMatchesBySpeaker(matches, activeTopics, playerProps.duration),
    [matches, activeTopics, playerProps.duration]
  );

  const speakerProps = {
    speakerTracks,
    segmentTree,
    talkTime,
    setTimePct,
    speakers,
    speakerMatches,
    sessionUser,
    dragStatus,
    setDragStatus,
    ...playerProps,
  };

  return (
    <div className="call-speaker-section">
      <ExpandToggle
        hideToggle={speakers.size <= 2}
        expanded={<CallSpeakerFull {...speakerProps} />}
        minimized={<CallSpeakerMini {...speakerProps} />}
      />
    </div>
  );
};

/**
 * Build up all speaker's tracks given the current track width
 * The track draws a line for points in the call where a speaker is speaking
 */
function buildSpeakerTracks(segments: List<Segment>, duration: Seconds) {
  if (!duration) return;

  let paths = Map<SpeakerNumber, string>();
  for (const segment of segments) {
    const pixelStart = segment.startTime / duration;
    const pixelEnd = segment.endTime / duration;
    // Move to the pixel where speaking starts, then draw a line from that point to
    // where the segment ends.
    const speakingSegment = `M ${pixelStart * 100},5 L ${pixelEnd * 100},5`;
    paths = paths.update(segment.speakerNumber, (path = '') => path + speakingSegment);
    paths = paths.update(MiniSpeakerNumber, (path = '') => path + speakingSegment);
  }

  return paths;
}
